/**
 * Out-of-combat inventory editing operations (ADR-0015 §1, §13; ADR-0022).
 *
 * Pure functions over `Inventory`: move a card to a cell on the loadout, rotate
 * it in place. Every operation is gated by `canPlace` (src/combat/grid.ts) — the
 * only placement authority — so the editor UI never invents its own geometry.
 * Returns a fresh `Inventory` on success, `null` when the move is illegal (the
 * UI rejects without mutating). Rearrangement is out-of-combat only (ADR-0015
 * §1); these are never called against a live combat loadout.
 *
 * ADR-0022 retired the stash: the loadout is the only card store, so `addCard`
 * places-or-declines into it (no fallback grid).
 */

import { canPlace } from '../grid';
import type { Card, Grid, GridPlacement, Inventory } from '../types';

/** The inventory has a single grid since ADR-0022 (stash retired). Kept as a
 * named type so callers and signatures stay self-documenting. */
export type GridName = 'loadout';

/** Every card id currently held in the loadout. */
function heldIds(inventory: Inventory): Set<string> {
  const ids = new Set<string>();
  for (const p of inventory.loadout.placements) ids.add(p.card.id);
  return ids;
}

/**
 * Return a copy of `card` whose id is unique within `inventory`, minting an
 * instance suffix (`<id>#2`, `#3`, …) when the player already holds that card
 * (HEX-63). Owned cards are keyed by `card.id` across the engine (assignment,
 * trap runtime, once-per-* sets, frozen map) and the editor, so a dropped or
 * bought duplicate must carry a distinct id or the two copies would share
 * runtime and collide in the grid. Cards carry all their data inline, so the
 * renamed copy stands alone — `cardById` is authoring/test-only and is never
 * called on owned instances. Returns `card` untouched when its id is free.
 */
export function mintUniqueCard(inventory: Inventory, card: Card): Card {
  const taken = heldIds(inventory);
  if (!taken.has(card.id)) return card;
  for (let n = 2; ; n++) {
    const id = `${card.id}#${n}`;
    if (!taken.has(id)) return { ...card, id };
  }
}

/** First legal cell for `card` on `grid`, scanned row-major, unrotated then
 * rotated. `null` if the card fits nowhere on this grid. */
export function firstFitOnGrid(
  grid: Grid,
  card: Card,
): { origin: { row: number; col: number }; rotated: boolean } | null {
  for (const rotated of [false, true]) {
    for (let row = 0; row < grid.capacity.rows; row++) {
      for (let col = 0; col < grid.capacity.cols; col++) {
        if (canPlace(grid.placements, grid.capacity, card, { row, col }, rotated)) {
          return { origin: { row, col }, rotated };
        }
      }
    }
  }
  return null;
}

/**
 * Add an owned card to the loadout (HEX-63 drops + shop purchases). The card is
 * minted to a unique id if it duplicates one already held, then first-fitted
 * into the loadout. Returns the updated inventory and where the card landed, or
 * `null` when it fits nowhere — the caller declines (place-or-decline, ADR-0022:
 * a kept drop is lost, a shop purchase is refused). Pure: the input inventory is
 * not mutated.
 */
export function addCard(
  inventory: Inventory,
  card: Card,
): { inventory: Inventory; grid: GridName; placement: GridPlacement } | null {
  const minted = mintUniqueCard(inventory, card);
  const fit = firstFitOnGrid(inventory.loadout, minted);
  if (fit === null) return null;
  const placement: GridPlacement = { card: minted, origin: fit.origin, rotated: fit.rotated };
  const next: Inventory = {
    ...inventory,
    loadout: { ...inventory.loadout, placements: [...inventory.loadout.placements, placement] },
  };
  return { inventory: next, grid: 'loadout', placement };
}

/** Whether `card` could be added to the loadout at all. */
export function canAddCard(inventory: Inventory, card: Card): boolean {
  return firstFitOnGrid(inventory.loadout, card) !== null;
}

/**
 * Place an *incoming* card into the loadout at a **chosen** cell — the HEX-149
 * acquisition flow, where the player picks where a gained/bought card lands
 * (rather than the first-fit `addCard` takes). The card is minted to a unique id
 * if it duplicates one held, then validated with `canPlace`. Returns the updated
 * inventory + placement, or `null` when the cell/rotation is illegal
 * (out-of-bounds or overlap) — the caller keeps the item in hand and the player
 * curates more. Pure: the input inventory is not mutated.
 */
export function placeCardAt(
  inventory: Inventory,
  card: Card,
  origin: { row: number; col: number },
  rotated: boolean,
): { inventory: Inventory; placement: GridPlacement } | null {
  const minted = mintUniqueCard(inventory, card);
  const { placements, capacity } = inventory.loadout;
  if (!canPlace(placements, capacity, minted, origin, rotated)) return null;
  const placement: GridPlacement = { card: minted, origin, rotated };
  return {
    inventory: {
      ...inventory,
      loadout: { ...inventory.loadout, placements: [...placements, placement] },
    },
    placement,
  };
}

/** Where a card currently sits, or null if the loadout does not hold it. */
export function findPlacement(
  inventory: Inventory,
  cardId: string,
): { grid: GridName; placement: GridPlacement } | null {
  const inLoadout = inventory.loadout.placements.find((p) => p.card.id === cardId);
  if (inLoadout) return { grid: 'loadout', placement: inLoadout };
  return null;
}

/**
 * Permanently drop the card with `cardId` from the loadout (HEX-81 sacrifice;
 * drawer curation, ADR-0022). Returns a fresh `Inventory` with the card filtered
 * out, or `null` when no card with that id is held (caller rejects). Pure: the
 * input inventory is not mutated.
 */
export function removeCard(inventory: Inventory, cardId: string): Inventory | null {
  if (findPlacement(inventory, cardId) === null) return null;
  return {
    ...inventory,
    loadout: {
      ...inventory.loadout,
      placements: inventory.loadout.placements.filter((p) => p.card.id !== cardId),
    },
  };
}

/**
 * Move `cardId` to `origin` on the loadout in the given rotation. The
 * destination is validated with the card removed from where it currently sits,
 * so a no-op nudge or a same-grid shuffle never collides with itself. Returns
 * the updated inventory, or null if the placement is out-of-bounds or overlaps
 * another card.
 */
export function moveCard(
  inventory: Inventory,
  cardId: string,
  toGrid: GridName,
  origin: { row: number; col: number },
  rotated: boolean,
): Inventory | null {
  const found = findPlacement(inventory, cardId);
  if (found === null) return null;

  const loadout = inventory.loadout.placements.filter((p) => p.card.id !== cardId);
  const bounds = inventory[toGrid].capacity;

  if (!canPlace(loadout, bounds, found.placement.card, origin, rotated)) return null;

  const moved: GridPlacement = { card: found.placement.card, origin, rotated };
  return {
    ...inventory,
    loadout: { ...inventory.loadout, placements: [...loadout, moved] },
  };
}

/**
 * Toggle a placed card's rotation at its current cell. Returns null when the
 * rotated footprint no longer fits (out-of-bounds or overlap) — the card stays
 * put and the UI rejects.
 */
export function rotateInPlace(inventory: Inventory, cardId: string): Inventory | null {
  const found = findPlacement(inventory, cardId);
  if (found === null) return null;
  return moveCard(inventory, cardId, found.grid, found.placement.origin, !found.placement.rotated);
}
